import _orderBy from "lodash/orderBy";
import _assign from "lodash/assign";

function _toConsumableArray(arr) { if (Array.isArray(arr)) { for (var i = 0, arr2 = Array(arr.length); i < arr.length; i++) { arr2[i] = arr[i]; } return arr2; } else { return Array.from(arr); } }

import { Helpers, LabelHelpers, Scale, Domain, Data } from "victory-core";

var sortData = function (dataset, sortKey) {
  var sortOrder = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : "ascending";

  if (!sortKey) {
    return dataset;
  }

  if (sortKey === "x" || sortKey === "y") {
    sortKey = "_" + sortKey;
  }

  var sortedData = _orderBy(dataset, sortKey);

  if (sortOrder === "descending") {
    return sortedData.reverse();
  }

  return sortedData;
};

var getData = function (props) {
  if (!props.data || Data.getLength(props.data) < 1) {
    return [];
  }
  var stringMap = {
    x: Data.createStringMap(props, "x")
  };

  var accessor = {
    x: Helpers.createAccessor(props.x !== undefined ? props.x : "x"),
    open: Helpers.createAccessor(props.open !== undefined ? props.open : "open"),
    close: Helpers.createAccessor(props.close !== undefined ? props.close : "close"),
    high: Helpers.createAccessor(props.high !== undefined ? props.high : "high"),
    low: Helpers.createAccessor(props.low !== undefined ? props.low : "low")
  };

  var formattedData = props.data.reduce(function (dataArr, datum, index) {
    datum = Data.parseDatum(datum);

    var evaluatedX = accessor.x(datum);
    var _x = evaluatedX !== undefined ? evaluatedX : index;
    var _open = accessor.open(datum);
    var _close = accessor.close(datum);
    var _high = accessor.high(datum);
    var _low = accessor.low(datum);
    var _y = [_open, _close, _high, _low];

    dataArr.push(_assign({}, datum, { _x: _x, _y: _y, _open: _open, _close: _close, _high: _high, _low: _low }, typeof _x === "string" ? { _x: stringMap.x[_x], x: _x } : {}));

    return dataArr;
  }, []);

  return sortData(formattedData, props.sortKey, props.sortOrder);
};

var getDomain = function (props, axis) {
  var domain = void 0;
  if (props.domain && props.domain[axis]) {
    domain = props.domain[axis];
  } else if (props.domain && Array.isArray(props.domain)) {
    domain = props.domain;
  } else {
    var dataset = getData(props);
    var allData = dataset.reduce(function (memo, datum) {
      return Array.isArray(datum["_" + axis]) ? memo.concat.apply(memo, _toConsumableArray(datum["_" + axis])) : memo.concat(datum["_" + axis]);
    }, []);

    if (allData.length < 1) {
      return Scale.getBaseScale(props, axis).domain();
    }

    var min = Math.min.apply(Math, _toConsumableArray(allData));
    var max = Math.max.apply(Math, _toConsumableArray(allData));
    if (+min === +max) {
      return Domain.getSinglePointDomain(max);
    }
    domain = [min, max];
  }
  return Domain.cleanDomain(Domain.padDomain(domain, props, axis), props);
};

var getCalculatedValues = function (props) {
  var theme = props.theme,
      polar = props.polar;

  var defaultStyle = theme && theme.candlestick && theme.candlestick.style ? theme.candlestick.style : {};
  var style = Helpers.getStyles(props.style, defaultStyle);
  var data = Data.addEventKeys(props, getData(props));
  var range = {
    x: Helpers.getRange(props, "x"),
    y: Helpers.getRange(props, "y")
  };
  var domain = {
    x: getDomain(props, "x"),
    y: getDomain(props, "y")
  };
  var scale = {
    x: Scale.getBaseScale(props, "x").domain(domain.x).range(range.x),
    y: Scale.getBaseScale(props, "y").domain(domain.y).range(range.y)
  };
  var origin = polar ? props.origin || Helpers.getPolarOrigin(props) : undefined;
  return { domain: domain, data: data, scale: scale, style: style, origin: origin };
};

var isTransparent = function (attr) {
  return attr === "none" || attr === "transparent";
};

var getDataStyles = function (datum, style, props) {
  style = style || {};
  var candleColor = datum.open > datum.close ? props.candleColors.negative : props.candleColors.positive;
  var fill = style.fill || candleColor;
  var strokeColor = style.stroke;
  var stroke = isTransparent(strokeColor) ? fill : strokeColor || "black";
  return _assign({}, style, { stroke: stroke, fill: fill });
};

var getLabelProps = function (dataProps, text, style) {
  var x = dataProps.x,
      high = dataProps.high,
      index = dataProps.index,
      scale = dataProps.scale,
      datum = dataProps.datum,
      data = dataProps.data;

  var labelStyle = style.labels || {};
  return {
    style: labelStyle,
    y: high - (labelStyle.padding || 0),
    x: x,
    text: text,
    index: index,
    scale: scale,
    datum: datum,
    data: data,
    textAnchor: labelStyle.textAnchor,
    verticalAnchor: labelStyle.verticalAnchor || "end",
    angle: labelStyle.angle
  };
};

var getBaseProps = function (props, fallbackProps) {
  // eslint-disable-line max-statements
  props = Helpers.modifyProps(props, fallbackProps, "candlestick");
  var calculatedValues = getCalculatedValues(props);
  var data = calculatedValues.data,
      style = calculatedValues.style,
      scale = calculatedValues.scale,
      domain = calculatedValues.domain,
      origin = calculatedValues.origin;
  var _props = props,
      groupComponent = _props.groupComponent,
      width = _props.width,
      height = _props.height,
      padding = _props.padding,
      standalone = _props.standalone,
      theme = _props.theme,
      polar = _props.polar,
      wickStrokeWidth = _props.wickStrokeWidth,
      labels = _props.labels,
      events = _props.events,
      sharedEvents = _props.sharedEvents;

  var initialChildProps = { parent: {
      domain: domain, scale: scale, width: width, height: height, data: data, standalone: standalone, theme: theme, polar: polar, origin: origin,
      style: style.parent, padding: padding
    } };

  return data.reduce(function (childProps, datum, index) {
    var eventKey = datum.eventKey || index;
    var x = scale.x(datum._x1 !== undefined ? datum._x1 : datum._x);
    var high = scale.y(datum._high);
    var close = scale.y(datum._close);
    var open = scale.y(datum._open);
    var low = scale.y(datum._low);
    var candleHeight = Math.abs(scale.y(datum._open) - scale.y(datum._close));
    var dataStyle = getDataStyles(datum, style.data, props);
    var dataProps = {
      x: x, high: high, low: low, candleHeight: candleHeight, scale: scale, data: data, datum: datum, groupComponent: groupComponent, index: index,
      style: dataStyle, padding: padding, width: width, polar: polar, origin: origin, wickStrokeWidth: wickStrokeWidth, open: open, close: close
    };

    childProps[eventKey] = {
      data: dataProps
    };
    var text = LabelHelpers.getText(props, datum, index);
    if (text !== undefined && text !== null || labels && (events || sharedEvents)) {
      childProps[eventKey].labels = getLabelProps(dataProps, text, style);
    }

    return childProps;
  }, initialChildProps);
};

export { getBaseProps, getDomain, getData };


//////////////////
// WEBPACK FOOTER
// ./~/victory-chart/es/components/victory-candlestick/helper-methods.js
// module id = 1752
// module chunks = 0